import { useState, useMemo, useEffect, useReducer, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import cn from 'classnames';
import { Settings } from 'react-slick';
import Slider from 'react-slick';
// API
import {
  GET_MEDIA_POSTS,
  GET_VIDEO_REQUESTS_FOR_STORE,
} from 'api/mediaPost/queries';
import {
  GET_NOT_REVIEWED_INTERVIEW_ANSWER_VIDEOS,
  GET_NOT_REVIEWED_ANY_AI_CLIP_CANDIDATES,
} from 'api/interview/queries';
// Types
import {
  GetMediaPostsInput,
  MediaPostsOrderBy,
  MediaPostStatus,
  SortDirection,
  StoreStatus,
  MediaPostType,
  VideoEditRequestStatus,
  VideoEditRequestSubmissionStatus,
} from 'api/graphql-global-types';
import {
  GetMediaPosts,
  GetMediaPostsVariables,
} from 'api/mediaPost/types/GetMediaPosts';
import {
  GetVideoEditRequestsForStore,
  GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities,
  GetVideoEditRequestsForStoreVariables,
} from 'api/mediaPost/types/GetVideoEditRequestsForStore';
import {
  GetNotReviewedInterviewAnswerVideos,
  GetNotReviewedInterviewAnswerVideos_getNotReviewedInterviewAnswerVideos,
  GetNotReviewedInterviewAnswerVideosVariables,
} from 'api/interview/types/GetNotReviewedInterviewAnswerVideos';
import {
  GetNotReviewedAnyAiClipCandidates,
  GetNotReviewedAnyAiClipCandidates_getNotReviewedAnyAiClipCandidates,
  GetNotReviewedAnyAiClipCandidatesVariables,
} from 'api/interview/types/GetNotReviewedAnyAiClipCandidates';
// Hooks
import { useGetCurrUser } from 'hooks';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useTrackViews, AnalyticsTargetType } from 'hooks/analytics';
// Constants
import {
  ATHLETES,
  SOCIAL_MEDIA_POST,
  VIDEO_SOCIAL_MEDIA_POST,
} from 'constants/routes';
// Components
import InfiniteScroll from 'components/common/InfiniteScroll/InfiniteScroll';
import NotFound from 'components/common/NotFound/NotFound';
import Section from 'components/common3/Section/Section';
import ProfileUnderReviewBanner from 'components/common3/ProfileUnderReviewBanner/ProfileUnderReviewBanner';
import Sorting, { SortingOptions } from 'components/common3/Sorting/Sorting';
import CarouselSlider from 'components/common3/CarouselSlider/CarouselSlider';
import VideoRequestCard from './VideoRequestCard/VideoRequestCard';
import InterviewAnswerVideoCard from './InterviewAnswerVideoCard/InterviewAnswerVideoCard';
import AiClipCandidateCard from './AiClipCandidateCard/AiClipCandidateCard';
import ReviewVideoRequestModal from './VideoRequestCard/ReviewVideoRequestModal/ReviewVideoRequestModal';
import ReviewInterviewAnswerVideoModal from './InterviewAnswerVideoCard/ReviewInterviewAnswerVideoModal/ReviewInterviewAnswerVideoModal';
import ReviewAiClipCandidateModal from './AiClipCandidateCard/ReviewAiClipCandidateModal/ReviewAiClipCandidateModal';
// Helpers
import { getLocaleDate, getUrlType, reducer, State } from 'helpers/mediaPost';
// UI
import FourDotsLoader from 'ui/FourDotsLoader/FourDotsLoader';
import Button from 'ui3/Button/Button';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import ContentCard, { ContentType } from 'ui3/Card/ContentCard';
import InfiniteScrollLoader from 'ui3/InfiniteScrollLoader/InfiniteScrollLoader';
// Styles
import styles from './MediaPostLibrary.module.scss';

export type MediaPostLibraryProps = {
  storeId?: string;
  storeSlug?: string;
  isPage?: boolean;
};

const PER_PAGE = 10;

const MediaPostLibrary = ({
  storeSlug,
  storeId,
  isPage = true,
}: MediaPostLibraryProps): JSX.Element | null => {
  const { data } = useGetCurrUser();
  const router = useRouter();
  const { isBreakpointL } = useBreakpoints();
  const isOwner = data?.me.id === storeId;
  const { type: urlType } = router.query;

  const [sorting, setSorting] = useState<SortingOptions>({
    direction: SortDirection.DESC,
    order: MediaPostsOrderBy.publishedAt,
  });

  const initialState: State = {
    mediaFilters: getUrlType(urlType as string),
    page: 1,
    reviewModalVisibility: false,
    videoRequestItem: null,
    interviewAnswerVideoModalVisibility: false,
    interviewAnswerVideoItem: null,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  // Standalone state for AI clip candidate modal — kept out of the reducer
  // so we don't have to touch helpers/mediaPost types.
  const [aiClipModalOpen, setAiClipModalOpen] = useState(false);
  const [
    selectedAiClipCandidate,
    setSelectedAiClipCandidate,
  ] = useState<GetNotReviewedAnyAiClipCandidates_getNotReviewedAnyAiClipCandidates | null>(
    null
  );
  const [
    selectedAiClipCandidateIndex,
    setSelectedAiClipCandidateIndex,
  ] = useState(1);

  const handleAiClipCandidateModalOpen = (
    candidate: GetNotReviewedAnyAiClipCandidates_getNotReviewedAnyAiClipCandidates,
    index: number
  ) => {
    setSelectedAiClipCandidate(candidate);
    setSelectedAiClipCandidateIndex(index);
    setAiClipModalOpen(true);
  };

  const handleCloseAiClipCandidateModal = () => {
    setAiClipModalOpen(false);
    setSelectedAiClipCandidate(null);
  };

  const carouselRef = useRef<Slider | null>(null);
  const feedViewSentIdsRef = useRef<Set<string>>(new Set());

  const { trackFeedViewBatch } = useTrackViews({
    targetType: AnalyticsTargetType.MEDIA_POST,
  });

  const storeInput = useMemo(() => {
    if (storeSlug) return { storeSlug };

    return {
      storeId,
      storeStatuses: [StoreStatus.Active, StoreStatus.Pending],
      statuses: isOwner
        ? [
            MediaPostStatus.published,
            MediaPostStatus.scheduled,
            MediaPostStatus.scheduledInstant,
          ]
        : [MediaPostStatus.published],
    };
    // storeSlug, storeId and isOwner wont change after initial load
  }, [storeSlug, storeId, isOwner]);

  const queryInput: GetMediaPostsInput = useMemo(() => {
    let type: MediaPostType[] | undefined;
    if (state.mediaFilters === 'video') {
      type = [MediaPostType.VideoPost];
    } else if (state.mediaFilters === 'image') {
      type = [MediaPostType.ImagePost];
    }

    return {
      direction: sorting.direction,
      orderBy: MediaPostsOrderBy.publishedAt,
      offset: 0,
      ...storeInput,
      type,
    };
  }, [sorting, storeInput, state.mediaFilters]);

  const showStoreOptions =
    isPage && (storeId ? data?.me.id === storeId : data?.me.slug === storeSlug);

  const {
    data: mediaPostsData,
    loading: isLoadingMedia,
    fetchMore: fetchMoreMedia,
  } = useQuery<GetMediaPosts, GetMediaPostsVariables>(GET_MEDIA_POSTS, {
    variables: {
      input: queryInput,
    },
    // Was `network-only`, which made the SSR-prefetched cache useless on
    // public tab pages (e.g. `/[athleteSlug]/media`) — every viewer hit
    // the network again after hydration, deferring LCP until the second
    // roundtrip resolved. `cache-and-network` lets the SSR cache paint
    // immediately while a background refresh keeps the list current for
    // the owner-edit flow.
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: videoRequestsData,
    loading: isLoadingRequests,
    refetch: refetchVideoRequests,
  } = useQuery<
    GetVideoEditRequestsForStore,
    GetVideoEditRequestsForStoreVariables
  >(GET_VIDEO_REQUESTS_FOR_STORE, {
    variables: {
      input: {
        storeId,
        statuses: [
          VideoEditRequestStatus.OnReview,
          VideoEditRequestStatus.Rejected,
        ],
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !storeId || !showStoreOptions,
  });

  const {
    data: interviewAnswerVideosData,
    refetch: refetchInterviewAnswerVideos,
  } = useQuery<
    GetNotReviewedInterviewAnswerVideos,
    GetNotReviewedInterviewAnswerVideosVariables
  >(GET_NOT_REVIEWED_INTERVIEW_ANSWER_VIDEOS, {
    variables: {
      input: {
        ownerId: storeId || '',
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !storeId || !showStoreOptions,
  });

  const { data: aiClipCandidatesData } = useQuery<
    GetNotReviewedAnyAiClipCandidates,
    GetNotReviewedAnyAiClipCandidatesVariables
  >(GET_NOT_REVIEWED_ANY_AI_CLIP_CANDIDATES, {
    variables: {
      input: {
        ownerId: storeId || '',
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !storeId || !showStoreOptions,
  });

  const total = mediaPostsData?.getMediaPosts?.total || 0;
  const hasMore =
    (mediaPostsData?.getMediaPosts?.entities?.length || 0) < total;
  const isNotFound = !isLoadingMedia && !total;

  const fetchMoreData = () => {
    const nextOffset = mediaPosts.length;

    fetchMoreMedia({
      variables: {
        input: {
          ...queryInput,
          offset: nextOffset,
        },
      },
    }).then(() => {
      dispatch({ type: 'INCREMENT_PAGE' });
    });
  };

  const handleMediaPostModalOpen = () => {
    router.push('/upload-new-media');
  };

  useEffect(() => {
    const type = getUrlType(urlType as string);
    if (type !== state.mediaFilters) {
      dispatch({ type: 'SET_FILTER', payload: type });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlType]);

  const mediaPosts = useMemo(
    () => mediaPostsData?.getMediaPosts?.entities || [],
    [mediaPostsData?.getMediaPosts?.entities]
  );
  const isAllMediaPosts = state.mediaFilters === 'all';
  const isVideoMediaPosts = state.mediaFilters === 'video';
  const isImageMediaPosts = state.mediaFilters === 'image';

  const displayedPosts = useMemo(() => {
    return mediaPosts.slice(0, state.page * PER_PAGE);
  }, [mediaPosts, state.page]);

  // Batch track feed views for all fetched posts (single request per load)
  useEffect(() => {
    if (displayedPosts.length === 0) return;
    const ids = displayedPosts.map((p) => p.id);
    const newIds = ids.filter((id) => !feedViewSentIdsRef.current.has(id));
    if (newIds.length > 0) {
      trackFeedViewBatch(newIds);
      newIds.forEach((id) => feedViewSentIdsRef.current.add(id));
    }
  }, [displayedPosts, trackFeedViewBatch]);

  useEffect(() => {
    dispatch({ type: 'RESET_PAGE' });
  }, [sorting]);

  if (!storeId) {
    return null;
  }

  const handleSetAllMediaPosts = () => {
    if (isAllMediaPosts) {
      return;
    }
    dispatch({ type: 'SET_FILTER', payload: 'all' });
  };

  const handleSetVideoMediaPosts = () => {
    if (isVideoMediaPosts) {
      return;
    }
    dispatch({ type: 'SET_FILTER', payload: 'video' });
  };

  const handleSetImageMediaPosts = () => {
    if (isImageMediaPosts) {
      return;
    }
    dispatch({ type: 'SET_FILTER', payload: 'image' });
  };

  // Find all the requests where any of the video clips is pending
  const videoRequests =
    videoRequestsData?.getVideoEditRequestsForStore.entities.filter((request) =>
      request.editedVideos.some((video) =>
        video.reviewSubmissions.some(
          (submission) =>
            submission.status === VideoEditRequestSubmissionStatus.Pending
        )
      )
    ) || [];

  const interviewAnswerVideos =
    interviewAnswerVideosData?.getNotReviewedInterviewAnswerVideos || [];

  const aiClipCandidates =
    aiClipCandidatesData?.getNotReviewedAnyAiClipCandidates || [];

  // Combine all three types into a single array
  type ReviewItem =
    | {
        type: 'videoRequest';
        data: GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities;
        index: number;
      }
    | {
        type: 'interviewAnswer';
        data: GetNotReviewedInterviewAnswerVideos_getNotReviewedInterviewAnswerVideos;
        index: number;
      }
    | {
        type: 'aiClipCandidate';
        data: GetNotReviewedAnyAiClipCandidates_getNotReviewedAnyAiClipCandidates;
        index: number;
      };

  // AI clips first so they're visible in the carousel (Interview Now
  // answers can pile up to 30+ entries and would otherwise hide them).
  const reviewItems: ReviewItem[] = [
    ...aiClipCandidates.map((candidate, index) => ({
      type: 'aiClipCandidate' as const,
      data: candidate,
      index: index + 1,
    })),
    ...videoRequests.map((request, index) => ({
      type: 'videoRequest' as const,
      data: request,
      index: aiClipCandidates.length + index + 1,
    })),
    ...interviewAnswerVideos.map((video, index) => ({
      type: 'interviewAnswer' as const,
      data: video,
      index: aiClipCandidates.length + videoRequests.length + index + 1,
    })),
  ];

  const handleReviewModalVisibility = (
    videoRequest: GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities
  ) => {
    dispatch({ type: 'SHOW_REVIEW_MODAL', payload: videoRequest });
    refetchVideoRequests();
  };

  const handleCloseReviewModalVisibility = () => {
    dispatch({ type: 'HIDE_REVIEW_MODAL' });
  };

  const handleInterviewAnswerVideoModalVisibility = (
    answerVideo: GetNotReviewedInterviewAnswerVideos_getNotReviewedInterviewAnswerVideos
  ) => {
    dispatch({
      type: 'SHOW_INTERVIEW_ANSWER_VIDEO_MODAL',
      payload: answerVideo,
    });
    refetchInterviewAnswerVideos();
  };

  const handleCloseInterviewAnswerVideoModalVisibility = () => {
    dispatch({ type: 'HIDE_INTERVIEW_ANSWER_VIDEO_MODAL' });
  };

  const showVideoRequests =
    !!reviewItems.length && !isLoadingRequests && showStoreOptions;

  // Show arrows if there are more items than can be displayed at once
  // Desktop: show if more than 3 items, Mobile: show if more than 1 item
  const shouldShowArrows = isBreakpointL
    ? reviewItems.length > 3
    : reviewItems.length > 1;

  const handleCarouselNext = () => {
    carouselRef.current?.slickNext();
  };

  const handleCarouselPrev = () => {
    carouselRef.current?.slickPrev();
  };

  const carouselSettings: Settings = {
    arrows: false,
    infinite: false,
  };

  return (
    <>
      {/*
        Only show the dots while we truly have no data yet. With
        `cache-and-network` the SSR cache paints instantly but `loading`
        stays true for the background refresh — flashing the loader over
        already-visible content was a bad UX swap and pushed perceived LCP.
      */}
      {isLoadingMedia && !mediaPostsData && <FourDotsLoader />}

      <ProfileUnderReviewBanner />
      <div
        className={cn(styles.root, {
          [styles.withPadding]: !isPage,
        })}
      >
        <Section title="" className={styles.section}>
          {showStoreOptions && (
            <div className={styles.mediaTitleContainer}>
              <span className={styles.mainTitle}>My Media Feed</span>
              <Button
                onClick={handleMediaPostModalOpen}
                variant="primary"
                size="medium"
              >
                <MillionsIcon name="plus" />
                New post
              </Button>
            </div>
          )}

          {showVideoRequests && (
            <>
              <div className={styles.reviewTitleContainer}>
                <p className={styles.title}>
                  Clips for review ({reviewItems.length})
                </p>
                {shouldShowArrows && (
                  <div className={styles.desktopCarouselNav}>
                    <button
                      className={styles.navButton}
                      onClick={handleCarouselPrev}
                      aria-label="Previous clip"
                    >
                      <MillionsIcon
                        name="arrowLeft"
                        className={cn(styles.arrowIcon, styles.left)}
                        size={20}
                      />
                    </button>
                    <button
                      className={styles.navButton}
                      onClick={handleCarouselNext}
                      aria-label="Next clip"
                    >
                      <MillionsIcon
                        name="arrowRight"
                        className={cn(styles.arrowIcon, styles.right)}
                        size={20}
                      />
                    </button>
                  </div>
                )}
              </div>

              <div className={styles.reviewCarouselWithArrows}>
                <div className={styles.reviewCarouselWrapper}>
                  <CarouselSlider ref={carouselRef} settings={carouselSettings}>
                    {reviewItems.map((item) => {
                      if (item.type === 'videoRequest') {
                        return (
                          <VideoRequestCard
                            key={`videoRequest-${item.data.id}`}
                            index={item.index}
                            videoRequest={item.data}
                            setReviewModalVisibility={
                              handleReviewModalVisibility
                            }
                          />
                        );
                      } else if (item.type === 'interviewAnswer') {
                        return (
                          <InterviewAnswerVideoCard
                            key={`interviewAnswer-${item.data.id}`}
                            index={item.index}
                            answerVideo={item.data}
                            setReviewModalVisibility={
                              handleInterviewAnswerVideoModalVisibility
                            }
                          />
                        );
                      } else {
                        return (
                          <AiClipCandidateCard
                            key={`aiClipCandidate-${item.data.id}`}
                            index={item.index}
                            candidate={item.data}
                            setReviewModalVisibility={
                              handleAiClipCandidateModalOpen
                            }
                          />
                        );
                      }
                    })}
                  </CarouselSlider>
                </div>
                {shouldShowArrows && (
                  <div className={styles.mobileCarouselNav}>
                    <button
                      className={styles.navButton}
                      onClick={handleCarouselPrev}
                      aria-label="Previous clip"
                    >
                      <MillionsIcon
                        name="arrowLeft"
                        className={cn(styles.arrowIcon, styles.left)}
                        size={20}
                      />
                    </button>
                    <button
                      className={styles.navButton}
                      onClick={handleCarouselNext}
                      aria-label="Next clip"
                    >
                      <MillionsIcon
                        name="arrowRight"
                        className={cn(styles.arrowIcon, styles.right)}
                        size={20}
                      />
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {showStoreOptions && (
            <div className={styles.filtersAndSortWrapper}>
              <div className={styles.filters}>
                <Button
                  onClick={handleSetAllMediaPosts}
                  variant="secondary"
                  size={isBreakpointL ? 'medium' : 'small'}
                  className={cn(styles.filterButton, styles.allFilterButton, {
                    [styles.selected]: isAllMediaPosts,
                  })}
                >
                  All
                </Button>
                <Button
                  onClick={handleSetVideoMediaPosts}
                  variant="secondary"
                  size={isBreakpointL ? 'medium' : 'small'}
                  className={cn(styles.filterButton, {
                    [styles.selected]: isVideoMediaPosts,
                  })}
                >
                  <MillionsIcon name="videoCamera" size={26} />
                  <span className={styles.filterButtonText}>Videos</span>
                </Button>
                <Button
                  onClick={handleSetImageMediaPosts}
                  variant="secondary"
                  size={isBreakpointL ? 'medium' : 'small'}
                  className={cn(styles.filterButton, {
                    [styles.selected]: isImageMediaPosts,
                  })}
                >
                  <MillionsIcon name="imageV2" size={18} />
                  <span className={styles.filterButtonText}>Images</span>
                </Button>
              </div>
              <div className={styles.sort}>
                <Sorting
                  wrapperClassName={styles.sectionSorting}
                  sortKey={'MediaPosts'}
                  sorting={sorting}
                  setSorting={setSorting}
                  showOnMobile
                  hideLabelOnMobile
                  size={isBreakpointL ? 'medium' : 'small'}
                />
              </div>
            </div>
          )}

          {!isNotFound && (
            <InfiniteScroll
              dataLength={displayedPosts.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={<InfiniteScrollLoader />}
              isOverflowVisible
            >
              <ul className={styles.mediaPostsWrapper}>
                {displayedPosts.map((mediaPost) => {
                  const isVideo = 'video' in mediaPost;
                  const thumbnailImage = isVideo
                    ? mediaPost.thumbnailUrl ??
                      (mediaPost.video.thumbnailUrl || '')
                    : mediaPost.image.url || '';

                  const date = mediaPost?.scheduledAt
                    ? getLocaleDate(mediaPost?.scheduledAt)
                    : mediaPost?.createdAt
                    ? getLocaleDate(mediaPost?.createdAt)
                    : '';

                  const mediaPostRoute = isVideo
                    ? VIDEO_SOCIAL_MEDIA_POST
                    : SOCIAL_MEDIA_POST;

                  return (
                    <li key={mediaPost.id}>
                      <ContentCard
                        type={isVideo ? ContentType.VIDEO : ContentType.IMAGE}
                        descriptionClassName={styles.contentCardInfoWrapper}
                        imageUrl={thumbnailImage}
                        linkUrl={`/${mediaPost.store.slug}${mediaPostRoute}${mediaPost.slug}`}
                        dateTime={date}
                        hasBottomLabel={false}
                        tzCode={mediaPost.scheduledAtTzCode || null}
                        title={mediaPost.title || ''}
                        userUrl={
                          mediaPost.store?.slug
                            ? `/${mediaPost.store?.slug}`
                            : ATHLETES
                        }
                        isPlay={isVideo}
                        hideCategory
                      />
                    </li>
                  );
                })}
              </ul>
            </InfiniteScroll>
          )}

          {isNotFound && <NotFound title="No media posts" />}
        </Section>
      </div>
      {state.videoRequestItem && (
        <ReviewVideoRequestModal
          storeId={storeId}
          videoRequest={state.videoRequestItem}
          isOpen={state.reviewModalVisibility}
          onClose={handleCloseReviewModalVisibility}
        />
      )}
      {state.interviewAnswerVideoItem && (
        <ReviewInterviewAnswerVideoModal
          storeId={storeId || ''}
          answerVideo={state.interviewAnswerVideoItem}
          isOpen={state.interviewAnswerVideoModalVisibility}
          onClose={handleCloseInterviewAnswerVideoModalVisibility}
        />
      )}
      {selectedAiClipCandidate && (
        <ReviewAiClipCandidateModal
          storeId={storeId || ''}
          candidate={selectedAiClipCandidate}
          index={selectedAiClipCandidateIndex}
          mediaPostsQueryInput={queryInput}
          isOpen={aiClipModalOpen}
          onClose={handleCloseAiClipCandidateModal}
        />
      )}
    </>
  );
};

export default MediaPostLibrary;
